import React from 'react';

import { useFormContext } from 'react-hook-form';
import { InputBox, LbInput, LbSelect } from '@infoods/lifebase-ui';

import classes from './FoodDetailsCard.module.scss';
import {
  LbCheckbox,
  LbNumericFormatInput,
} from '../../../../shared/components/form-components';
import { FOOD_TYPE, FoodType } from '../../../constants/foodType';
import { FoodBrand } from '../../../interfaces/FoodBrand';
import { FoodTag } from '../../../interfaces/FoodTag';
import { useRescaleNutrients } from '../hooks/useRescaleNutrients';
import { FoodFormValues } from '../interfaces/FoodFormValues';
import { SupplementFields } from './SupplementFields/SupplementFields';
import { TagsField } from './TagsField/TagsField';
import { Option } from '../../../../general-types';
import { Measurement } from '../../../interfaces/Measurement';

type FoodDetailsCardProps = {
  foodType: FoodType;
  amountUnitOptions: Option[];
  /** null for a manual food - Unit amount and unit changes then leave the nutrients alone */
  nxMeasurementsByUnit: Record<string, Measurement> | null;
  tags: FoodTag[];
  brands: FoodBrand[];
};

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 9999;

const FoodDetailsCard = ({
  foodType,
  amountUnitOptions,
  nxMeasurementsByUnit,
  tags,
  brands,
}: FoodDetailsCardProps) => {
  const isSupplement = foodType === FOOD_TYPE.SUPPLEMENT;
  const { control } = useFormContext<FoodFormValues>();

  useRescaleNutrients(nxMeasurementsByUnit);

  return (
    <InputBox title={`${isSupplement ? 'Supplement' : 'Food'} Details`}>
      <LbInput
        control={control}
        name="name"
        label="Product name"
        rules={{ required: 'Product is required' }}
        className={classes.productNameInput}
      />
      <LbCheckbox
        control={control}
        name="recommended"
        label="Recommended"
        rules={{ deps: ['tags'] }}
      />
      <LbSelect
        control={control}
        name="amountUnit"
        label="Unit Description"
        options={amountUnitOptions}
        rules={{ required: 'Units is required' }}
      />
      <LbNumericFormatInput
        control={control}
        name="recommendedServingSize"
        label="Unit Amount"
        rules={{
          required: 'Amount is required',
          min: { value: MIN_AMOUNT, message: 'Value must be greater than or equal to 1' },
          max: { value: MAX_AMOUNT, message: 'Value must be less than 9999' },
        }}
        allowNegative={false}
        decimalScale={3}
        selectValueOnFocus
      />
      <LbInput
        control={control}
        name="barcode"
        label="Barcode"
        rules={{
          required: foodType === FOOD_TYPE.BARCODE && 'Barcode is required',
        }}
      />
      {isSupplement && <SupplementFields brands={brands} />}
      <div className={classes.tagsField}>
        <TagsField tags={tags} />
      </div>
    </InputBox>
  );
};

export { FoodDetailsCard };
